/*
 * Your Hall data layer  ·  assets/hoh-hall-data.js
 *
 * The single source of truth for the user's personal Hall. Everything
 * the user curates (saved heroes, role tags, location notes, parties,
 * saved scenes/taverns/companies/summons) lives in localStorage under
 * two schema-versioned keys:
 *
 *   hoh-saved-v1     The user's hall. Heroes plus parties plus saved
 *                    Satchel results.
 *   hoh-removed-v1   Heroes the user has explicitly removed. Acts as a
 *                    suppression layer so a re-heart can re-add them.
 *
 * Hearting a character auto saves them through hohHallSaveHero(id, 'heart'),
 * UNLESS the id is in the removed list. The removed list clears for an
 * id whenever the user explicitly re-saves or removes-the-removal.
 *
 * Removing from the hall is local only. The community heart count, the
 * server-side ticket count, and the original submitter's stats are never
 * touched. The only thing removal affects is which heroes the user sees
 * in their Hall and which ones the Cast Only filters surface.
 *
 * Migration: the first time this file runs and finds no hoh-saved-v1,
 * it seeds heroes from the legacy hoh-hearts localStorage map so users
 * who hearted characters before this feature shipped land on a populated
 * Hall instead of an empty state.
 *
 * Public API (mounted on window):
 *
 *   hohHallGet()                                  read-only snapshot
 *   hohHallIsSaved(id)                            boolean
 *   hohHallIsRemoved(id)                          boolean
 *   hohHallSaveHero(id, source)                   add or no-op (respects removed)
 *   hohHallForceSaveHero(id)                      add and clear removed flag
 *   hohHallRemoveHero(id)                         remove and add to removed
 *   hohHallToggleHero(id)                         convenience for UI star
 *   hohHallTagHero(id, role, on)                  add/remove a role on a hero
 *   hohHallGetRoles(id)                           string[] of role keys
 *   hohHallSetLocation(id, location)              set or clear location string
 *   hohHallSetNotes(id, notes)                    short notes field
 *   hohHallReorder(orderedIds)                    custom order
 *   hohHallGetHeroes()                            current saved heroes array
 *   hohHallSavedIdSet()                           Set of saved ids (fast)
 *   hohHallCreateParty(name, color)               create a party
 *   hohHallUpdateParty(partyId, patch)            patch fields
 *   hohHallDeleteParty(partyId)                   remove
 *   hohHallSetPartySlot(partyId, idx, heroId)     fill or clear a slot
 *   hohHallReorderPartySlots(partyId, ids)        drag reorder
 *   hohHallMigrateFromHearts()                    one-time seed
 *   hohHallSubscribe(handler)                     change-event listener
 *
 * Roles: 'ally', 'rival', 'boss', 'patron', 'quest_giver', 'background'
 *
 * Storage shape (v1):
 *   {
 *     schemaVersion: 1,
 *     heroes:    [{ id, addedAt, addedVia, campaignId, roles, location, notes, orderIndex }],
 *     parties:   [{ id, name, color, slots, createdAt, updatedAt, lastRunAt, campaignId }],
 *     scenes:    [{ id, who, want, complication, savedAt, campaignId }],
 *     taverns:   [{ id, tavernName, atmosphere, special, patron, rumor, savedAt, campaignId }],
 *     companies: [{ id, comboHash, scenarioKey, memberIds, hookline, savedAt, campaignId }],
 *     summons:   [{ id, characterId, archetype, savedAt, campaignId }],
 *     meta:      { lastUsedTab, lastVisitedAt, migratedFromHearts }
 *   }
 */
(function(){
  'use strict';

  var SAVED_KEY   = 'hoh-saved-v1';
  var REMOVED_KEY = 'hoh-removed-v1';
  var DEFAULT_CAMPAIGN = 'default';
  var SCHEMA = 1;

  /* The full preset role set. Order matters for the chip strip. */
  var ROLE_PRESETS = ['ally','rival','boss','patron','quest_giver','background'];
  var ROLE_LABELS = {
    ally:        'Ally',
    rival:       'Rival',
    boss:        'Boss',
    patron:      'Patron',
    quest_giver: 'Quest Giver',
    background:  'Background'
  };

  /* Subscribers are notified after every mutating call so render layers
     can re-paint without each layer wiring its own DOM events. Pure
     in-memory; nothing persists between page loads. */
  var subscribers = [];

  function notify(kind){
    for(var i=0;i<subscribers.length;i++){
      try { subscribers[i](kind); } catch(e){}
    }
  }

  /* ------------------------------------------------------------------ */
  /* Storage primitives                                                 */
  /* ------------------------------------------------------------------ */

  function emptyHall(){
    return {
      schemaVersion: SCHEMA,
      heroes: [],
      parties: [],
      scenes: [],
      taverns: [],
      companies: [],
      summons: [],
      meta: { lastUsedTab: 'cast', lastVisitedAt: Date.now(), migratedFromHearts: false }
    };
  }

  function readSaved(){
    try {
      var raw = localStorage.getItem(SAVED_KEY);
      if(!raw) return emptyHall();
      var parsed = JSON.parse(raw);
      if(!parsed || parsed.schemaVersion !== SCHEMA) return migrateLegacy(parsed) || emptyHall();
      /* Defensive defaults for any field a future write might have skipped. */
      parsed.heroes    = Array.isArray(parsed.heroes)    ? parsed.heroes    : [];
      parsed.parties   = Array.isArray(parsed.parties)   ? parsed.parties   : [];
      parsed.scenes    = Array.isArray(parsed.scenes)    ? parsed.scenes    : [];
      parsed.taverns   = Array.isArray(parsed.taverns)   ? parsed.taverns   : [];
      parsed.companies = Array.isArray(parsed.companies) ? parsed.companies : [];
      parsed.summons   = Array.isArray(parsed.summons)   ? parsed.summons   : [];
      parsed.meta      = parsed.meta || { lastUsedTab: 'cast', lastVisitedAt: Date.now(), migratedFromHearts: false };
      return parsed;
    } catch(e){
      return emptyHall();
    }
  }

  function writeSaved(state){
    try {
      state.meta = state.meta || {};
      state.meta.lastVisitedAt = Date.now();
      localStorage.setItem(SAVED_KEY, JSON.stringify(state));
    } catch(e){
      /* Quota or private mode. Swallow so the UI keeps working in
         memory even when persistence fails. */
    }
  }

  function readRemoved(){
    try {
      var raw = localStorage.getItem(REMOVED_KEY);
      if(!raw) return { schemaVersion: SCHEMA, ids: [] };
      var parsed = JSON.parse(raw);
      if(!parsed || !Array.isArray(parsed.ids)) return { schemaVersion: SCHEMA, ids: [] };
      return parsed;
    } catch(e){
      return { schemaVersion: SCHEMA, ids: [] };
    }
  }

  function writeRemoved(state){
    try { localStorage.setItem(REMOVED_KEY, JSON.stringify(state)); } catch(e){}
  }

  function migrateLegacy(parsed){
    /* Future hook. Today there's only schemaVersion 1, so any older
       payload is treated as missing. We keep the function so a v2
       upgrade later can convert without losing data. */
    return null;
  }

  /* ------------------------------------------------------------------ */
  /* Hero CRUD                                                          */
  /* ------------------------------------------------------------------ */

  function findHero(state, id){
    for(var i=0;i<state.heroes.length;i++){
      if(state.heroes[i] && state.heroes[i].id === id) return state.heroes[i];
    }
    return null;
  }

  function nextOrderIndex(state){
    var max = -1;
    for(var i=0;i<state.heroes.length;i++){
      var oi = state.heroes[i] && state.heroes[i].orderIndex;
      if(typeof oi === 'number' && oi > max) max = oi;
    }
    return max + 1;
  }

  window.hohHallGet = function(){ return readSaved(); };

  window.hohHallIsSaved = function(id){
    if(!id) return false;
    var s = readSaved();
    return !!findHero(s, id);
  };

  window.hohHallIsRemoved = function(id){
    if(!id) return false;
    var r = readRemoved();
    return r.ids.indexOf(id) !== -1;
  };

  window.hohHallSavedIdSet = function(){
    var s = readSaved();
    var set = Object.create(null);
    for(var i=0;i<s.heroes.length;i++){
      if(s.heroes[i] && s.heroes[i].id) set[s.heroes[i].id] = true;
    }
    return set;
  };

  window.hohHallGetHeroes = function(){
    return readSaved().heroes.slice();
  };

  window.hohHallGetRoles = function(id){
    var h = findHero(readSaved(), id);
    return h && Array.isArray(h.roles) ? h.roles.slice() : [];
  };

  /* Auto-save path. Respects the removed list — a heart on a hero the
     user explicitly removed does NOT re-add them. The user has to either
     hit the explicit Save star or call hohHallForceSaveHero. */
  window.hohHallSaveHero = function(id, source){
    if(!id) return false;
    var rem = readRemoved();
    if(rem.ids.indexOf(id) !== -1) return false;
    var s = readSaved();
    if(findHero(s, id)) return false;
    s.heroes.push({
      id: id,
      addedAt: Date.now(),
      addedVia: source || 'save',
      campaignId: DEFAULT_CAMPAIGN,
      roles: [],
      location: null,
      notes: null,
      orderIndex: nextOrderIndex(s)
    });
    writeSaved(s);
    notify('hero-added');
    return true;
  };

  /* Force path. Used by the explicit star toggle when the user wants to
     undo an earlier removal. Clears the removed flag if present, then
     adds the hero. */
  window.hohHallForceSaveHero = function(id, source){
    if(!id) return false;
    var rem = readRemoved();
    var idx = rem.ids.indexOf(id);
    if(idx !== -1){
      rem.ids.splice(idx, 1);
      writeRemoved(rem);
    }
    var s = readSaved();
    if(findHero(s, id)) return false;
    s.heroes.push({
      id: id,
      addedAt: Date.now(),
      addedVia: source || 'save',
      campaignId: DEFAULT_CAMPAIGN,
      roles: [],
      location: null,
      notes: null,
      orderIndex: nextOrderIndex(s)
    });
    writeSaved(s);
    notify('hero-added');
    return true;
  };

  window.hohHallRemoveHero = function(id){
    if(!id) return false;
    var s = readSaved();
    var idx = -1;
    for(var i=0;i<s.heroes.length;i++){
      if(s.heroes[i] && s.heroes[i].id === id){ idx = i; break; }
    }
    if(idx === -1) return false;
    s.heroes.splice(idx, 1);
    /* Also drop this hero from any party slot, otherwise the slot
       renderer paints a ghost portrait that resolves to nothing. */
    for(var p=0;p<s.parties.length;p++){
      var party = s.parties[p];
      if(!party || !Array.isArray(party.slots)) continue;
      for(var k=0;k<party.slots.length;k++){
        if(party.slots[k] === id){
          party.slots[k] = null;
          party.updatedAt = Date.now();
        }
      }
    }
    writeSaved(s);
    /* Mark as explicitly removed so the auto-heart-save path skips. */
    var rem = readRemoved();
    if(rem.ids.indexOf(id) === -1){
      rem.ids.push(id);
      writeRemoved(rem);
    }
    notify('hero-removed');
    return true;
  };

  window.hohHallToggleHero = function(id){
    if(window.hohHallIsSaved(id)){
      return window.hohHallRemoveHero(id);
    }
    return window.hohHallForceSaveHero(id, 'star');
  };

  /* Tag a hero with a role. Caps total roles at 3 to keep cards visually
     calm; the limit is enforced silently (further taps no-op). */
  window.hohHallTagHero = function(id, role, on){
    if(!id || !role) return false;
    var s = readSaved();
    var h = findHero(s, id);
    if(!h){
      /* Tagging an unsaved hero auto-saves them first. Easier mental
         model than two-step "save then tag." */
      window.hohHallForceSaveHero(id, 'tag');
      s = readSaved();
      h = findHero(s, id);
      if(!h) return false;
    }
    h.roles = Array.isArray(h.roles) ? h.roles : [];
    var idx = h.roles.indexOf(role);
    if(on){
      if(idx === -1){
        if(h.roles.length >= 3) return false;
        h.roles.push(role);
      }
    } else {
      if(idx !== -1) h.roles.splice(idx, 1);
    }
    writeSaved(s);
    notify('hero-tagged');
    return true;
  };

  window.hohHallSetLocation = function(id, location){
    if(!id) return false;
    var s = readSaved();
    var h = findHero(s, id);
    if(!h) return false;
    h.location = location ? String(location).slice(0, 80) : null;
    writeSaved(s);
    notify('hero-meta');
    return true;
  };

  window.hohHallSetNotes = function(id, notes){
    if(!id) return false;
    var s = readSaved();
    var h = findHero(s, id);
    if(!h) return false;
    h.notes = notes ? String(notes).slice(0, 280) : null;
    writeSaved(s);
    notify('hero-meta');
    return true;
  };

  window.hohHallReorder = function(orderedIds){
    if(!Array.isArray(orderedIds)) return false;
    var s = readSaved();
    var byId = {};
    for(var i=0;i<s.heroes.length;i++) byId[s.heroes[i].id] = s.heroes[i];
    var rebuilt = [];
    var idx = 0;
    /* First pass: items the caller listed, in the order given. */
    for(var k=0;k<orderedIds.length;k++){
      var h = byId[orderedIds[k]];
      if(h){ h.orderIndex = idx++; rebuilt.push(h); delete byId[orderedIds[k]]; }
    }
    /* Second pass: anything the caller didn't mention keeps its
       relative order, appended after. Defensive against partial
       reorder calls. */
    for(var key in byId){
      if(Object.prototype.hasOwnProperty.call(byId, key)){
        byId[key].orderIndex = idx++;
        rebuilt.push(byId[key]);
      }
    }
    s.heroes = rebuilt;
    writeSaved(s);
    notify('hero-reordered');
    return true;
  };

  /* Jan (2026-09-27): "dont save the roll history for the dm tools". Summon,
     Scene, Tavern and Company no longer write their results here, so the
     save and remove calls for them are gone. Lists an older visit stored
     under scenes/taverns/companies/summons stay in hoh-saved-v1 untouched;
     nothing reads them any more. */
  function genId(prefix){
    return (prefix || 'h-') + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2,8);
  }

  /* ------------------------------------------------------------------ */
  /* Parties                                                            */
  /* ------------------------------------------------------------------ */

  var PARTY_COLORS = ['gold','crimson','frost','green','purple','blue'];
  var DEFAULT_PARTY_SIZE = 6;

  function newParty(name, color){
    return {
      id: genId('p-'),
      name: name || 'My Party',
      color: PARTY_COLORS.indexOf(color) !== -1 ? color : 'gold',
      slots: new Array(DEFAULT_PARTY_SIZE).fill(null),
      createdAt: Date.now(),
      updatedAt: Date.now(),
      lastRunAt: null,
      campaignId: DEFAULT_CAMPAIGN
    };
  }

  window.hohHallCreateParty = function(name, color){
    var s = readSaved();
    var p = newParty(name, color);
    s.parties.push(p);
    writeSaved(s);
    notify('party-created');
    return p;
  };

  window.hohHallUpdateParty = function(partyId, patch){
    if(!partyId) return false;
    var s = readSaved();
    for(var i=0;i<s.parties.length;i++){
      if(s.parties[i].id === partyId){
        var allowed = {};
        if(patch && typeof patch.name === 'string') allowed.name = patch.name.slice(0, 40);
        if(patch && typeof patch.color === 'string' && PARTY_COLORS.indexOf(patch.color) !== -1) allowed.color = patch.color;
        if(patch && typeof patch.lastRunAt === 'number') allowed.lastRunAt = patch.lastRunAt;
        Object.assign(s.parties[i], allowed);
        s.parties[i].updatedAt = Date.now();
        writeSaved(s);
        notify('party-updated');
        return true;
      }
    }
    return false;
  };

  window.hohHallDeleteParty = function(partyId){
    if(!partyId) return false;
    var s = readSaved();
    var idx = -1;
    for(var i=0;i<s.parties.length;i++){ if(s.parties[i].id === partyId){ idx = i; break; } }
    if(idx === -1) return false;
    s.parties.splice(idx, 1);
    writeSaved(s);
    notify('party-deleted');
    return true;
  };

  window.hohHallSetPartySlot = function(partyId, slotIdx, heroId){
    if(!partyId) return false;
    var s = readSaved();
    for(var i=0;i<s.parties.length;i++){
      if(s.parties[i].id === partyId){
        if(!Array.isArray(s.parties[i].slots) || s.parties[i].slots.length !== DEFAULT_PARTY_SIZE){
          s.parties[i].slots = new Array(DEFAULT_PARTY_SIZE).fill(null);
        }
        if(slotIdx < 0 || slotIdx >= DEFAULT_PARTY_SIZE) return false;
        /* Prevent the same hero appearing in two slots within one party. */
        if(heroId){
          for(var k=0;k<s.parties[i].slots.length;k++){
            if(k !== slotIdx && s.parties[i].slots[k] === heroId){
              s.parties[i].slots[k] = null;
            }
          }
        }
        s.parties[i].slots[slotIdx] = heroId || null;
        s.parties[i].updatedAt = Date.now();
        writeSaved(s);
        notify('party-slot');
        return true;
      }
    }
    return false;
  };

  window.hohHallReorderPartySlots = function(partyId, ids){
    if(!partyId || !Array.isArray(ids)) return false;
    var s = readSaved();
    for(var i=0;i<s.parties.length;i++){
      if(s.parties[i].id === partyId){
        var next = new Array(DEFAULT_PARTY_SIZE).fill(null);
        for(var k=0;k<DEFAULT_PARTY_SIZE && k<ids.length;k++) next[k] = ids[k] || null;
        s.parties[i].slots = next;
        s.parties[i].updatedAt = Date.now();
        writeSaved(s);
        notify('party-slot');
        return true;
      }
    }
    return false;
  };

  /* ------------------------------------------------------------------ */
  /* Migration: seed from hoh-hearts                                    */
  /* ------------------------------------------------------------------ */

  window.hohHallMigrateFromHearts = function(){
    var s = readSaved();
    if(s.meta && s.meta.migratedFromHearts) return false;
    var heartMap = {};
    try {
      var raw = localStorage.getItem('hoh-hearts');
      if(raw) heartMap = JSON.parse(raw) || {};
    } catch(e){}
    var ids = Object.keys(heartMap).filter(function(id){ return (heartMap[id]||0) > 0; });
    /* Sort by descending personal heart count so the user's most loved
       characters land at the top of their cast on first open. */
    ids.sort(function(a,b){ return (heartMap[b]||0) - (heartMap[a]||0); });
    var added = 0;
    var rem = readRemoved();
    var existing = {};
    for(var i=0;i<s.heroes.length;i++) existing[s.heroes[i].id] = true;
    for(var k=0;k<ids.length;k++){
      var id = ids[k];
      if(existing[id]) continue;
      if(rem.ids.indexOf(id) !== -1) continue;
      s.heroes.push({
        id: id,
        addedAt: Date.now(),
        addedVia: 'heart',
        campaignId: DEFAULT_CAMPAIGN,
        roles: [],
        location: null,
        notes: null,
        orderIndex: s.heroes.length
      });
      added++;
    }
    s.meta = s.meta || {};
    s.meta.migratedFromHearts = true;
    writeSaved(s);
    if(added > 0) notify('migrated');
    return added;
  };

  /* ------------------------------------------------------------------ */
  /* Subscription                                                        */
  /* ------------------------------------------------------------------ */

  window.hohHallSubscribe = function(handler){
    if(typeof handler !== 'function') return function(){};
    subscribers.push(handler);
    return function unsubscribe(){
      var i = subscribers.indexOf(handler);
      if(i !== -1) subscribers.splice(i, 1);
    };
  };

  /* Cross-tab sync: when another tab mutates the hall, fire a notify so
     visible UI re-renders. Cheap. */
  try {
    window.addEventListener('storage', function(e){
      if(!e || !e.key) return;
      if(e.key === SAVED_KEY || e.key === REMOVED_KEY) notify('storage');
    });
  } catch(e){}

  /* ------------------------------------------------------------------ */
  /* Constants exported for renderers                                   */
  /* ------------------------------------------------------------------ */

  window.HOH_HALL_ROLE_PRESETS = ROLE_PRESETS;
  window.HOH_HALL_ROLE_LABELS  = ROLE_LABELS;
  window.HOH_HALL_PARTY_COLORS = PARTY_COLORS;
  window.HOH_HALL_PARTY_SIZE   = DEFAULT_PARTY_SIZE;

  /* Run migration eagerly so other modules that read the hall don't see
     an empty list before the user opens Your Hall. Safe — migration is
     idempotent and does nothing on the second call. */
  try { window.hohHallMigrateFromHearts(); } catch(e){}

  /* ------------------------------------------------------------------ */
  /* Modal rehoming                                                     */
  /* ------------------------------------------------------------------ */
  /* In Your Hall mode (.hoh-page-mode-yours), hall-of-heroes.css line
     5619 hides every direct child of .hoh-content with !important. The
     tool modals (Company, Scene, Tavern, Detail, etc.) are direct
     children of .hoh-content, so when JS sets their inline
     display:flex to open them, the parent rule's !important wins and
     the modal stays invisible — Run This Party / Roll a Scene / Roll
     a Tavern / Open Detail all appear silent.

     The bulletproof fix: physically move the modal elements out of
     .hoh-content and into document.body. They become siblings of
     .hoh-content, so the hide rule cannot match them by selector.
     No CSS specificity battles, no caching gotchas. */
  function rehomeModalsToBody(){
    var modalIds = [
      'hoh-company-modal',
      'hoh-scene-modal',
      'hoh-tavern-modal',
      'hoh-dice-modal',
      'hoh-coin-modal',
      'hoh-modal',
      'hoh-explain-modal',
      'hoh-form-modal',
      'hoh-trial-modal',
      'hoh-ff-qualify-modal'
    ];
    for(var i=0;i<modalIds.length;i++){
      var m = document.getElementById(modalIds[i]);
      if(m && m.parentElement && m.parentElement !== document.body){
        document.body.appendChild(m);
      }
    }
  }
  /* Run as soon as the DOM is parsed. Defer-loaded scripts (us) execute
     after HTML parsing but before DOMContentLoaded — so the modals
     already exist in the DOM when this runs. The body element also
     exists. We're safe to mutate. */
  try {
    if(document.body) rehomeModalsToBody();
    /* Safety net in case any modal is rendered later in the page (e.g.,
       a Liquid render that streams in). Re-run on DOMContentLoaded
       (idempotent — already-moved modals stay in body). */
    if(document.readyState === 'loading'){
      document.addEventListener('DOMContentLoaded', rehomeModalsToBody);
    }
  } catch(e){}
})();
